import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, withKeys as _withKeys, withCtx as _withCtx, Fragment as _Fragment, resolveComponent as _resolveComponent } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "brandHeader"
};
const _hoisted_2 = ["src"];
const _hoisted_3 = ["src"];
const _hoisted_4 = { class: "signInPage" };
const _hoisted_5 = {
    key: 0,
    class: "signInHeading"
};
const _hoisted_6 = {
    key: 1,
    class: "signInSubtitle"
};
const _hoisted_7 = {
    key: 0,
    class: "helpAvatar"
};
const _hoisted_8 = ["src"];
const _hoisted_9 = { class: "helpBody" };
const _hoisted_10 = { class: "helpCopy" };
const _hoisted_11 = { class: "helpTitle" };
const _hoisted_12 = ["src"];
const _hoisted_13 = { class: "helpText" };
const _hoisted_14 = ["src"];
const _hoisted_15 = {
    key: 0,
    class: "fieldError"
};
const _hoisted_16 = { class: "passwordFields" };
const _hoisted_17 = { class: "emailRow" };
const _hoisted_18 = { class: "emailValue" };
const _hoisted_19 = ["src"];
const _hoisted_20 = {
    key: 0,
    class: "passwordError"
};
const _hoisted_21 = { class: "passwordErrorTitle" };
const _hoisted_22 = ["src"];
const _hoisted_23 = { class: "signInActions" };
import { ref, computed, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useApi, setApiConfig } from '@/composables/useApi';
import { useAppState } from '@/composables/useAppState';
import { signupUrl } from '@/config';
import { useCustomer } from '@/composables/useCustomer';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import MGTextField from './elements/MGTextField.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'SignInPage',
    setup(__props) {
        const route = useRoute();
        const router = useRouter();
        const api = useApi();
        const state = useAppState();
        const { checkEmail } = useCustomer();
        const instance = getCurrentInstance();
        const cookies = instance?.appContext.config.globalProperties.$cookies;
        // Sign in happens before there's a session, so the API config is set from route meta
        // rather than via useAuth() — same as the existing LoginPage.
        const apiUrl = route.meta.api;
        const configUri = route.meta.configUri;
        state.api = apiUrl;
        state.configUri = configUri;
        setApiConfig({
            api: apiUrl,
            configUri: configUri,
            useCache: state.useCache,
            useCachedEndpoints: state.useCachedEndpoints,
            cachedBlacklist: state.cachedBlacklist,
            cookies: cookies,
            router: router,
        });
        const email = ref('');
        const password = ref('');
        const showPassword = ref(false);
        const error = ref('');
        const checking = ref(false);
        // null until the address has been checked and rejected
        const rejectedStatus = ref(null);
        // A step ref, not a route query: App.vue keys the router-view on the query, so a query
        // change would remount this page and lose the email. The header back arrow steps back.
        const step = ref('email');
        const rejected = computed(() => rejectedStatus.value !== null);
        const eligible = computed(() => rejectedStatus.value === 'EMPLOYER_KNOWN');
        // No name on the password step: nothing resolves an email to a name before sign in
        const headerTitle = computed(() => {
            if (eligible.value)
                return 'Activate your membership';
            if (rejected.value)
                return 'Email not recognised';
            return step.value === 'email' ? "Sign in" : 'Welcome back';
        });
        // The API returns a status only, so the employer can't be named as the design does
        const cardTitle = computed(() => {
            if (eligible.value)
                return "You're eligible through your employer";
            if (rejected.value)
                return "Haven't activated your membership yet?";
            return 'New to Money Guided?';
        });
        const cardText = computed(() => {
            if (eligible.value)
                return 'Your employer gives you access to Money Guided. Activate your membership to create your account. It only takes about 2 minutes.';
            if (rejected.value)
                return 'If you get Money Guided through your employer, activate your membership to create your account. It only takes about 2 minutes.';
            return 'If you get Money Guided through your employer, activate your membership and create your account. It only takes about 2 minutes.';
        });
        const cardIcon = computed(() => {
            if (eligible.value)
                return require('@/assets/signin/info-green-16px.svg');
            if (rejected.value)
                return require('@/assets/signin/info-16px.svg');
            return '';
        });
        function toPassword() {
            if (!email.value || checking.value)
                return;
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
                error.value = 'Please enter a valid email address';
                return;
            }
            error.value = '';
            checking.value = true;
            checkEmail(email.value, (status) => {
                checking.value = false;
                if (status === 'CUSTOMER_KNOWN') {
                    step.value = 'password';
                    return;
                }
                // EMPLOYER_KNOWN means they're eligible but have no account yet; both
                // non-customer cases keep this screen and swap the card and error copy
                error.value = status === 'EMPLOYER_KNOWN'
                    ? 'Try using your personal email'
                    : "We can't find an account for this email.";
                rejectedStatus.value = status;
            });
        }
        // Signup lives outside the app; publish.sh / prodPublish.sh swap the host per environment.
        // Hand the address over so it needn't be retyped — www's Signup.vue reads ?email=.
        function activateMembership() {
            const url = email.value
                ? signupUrl + '?email=' + encodeURIComponent(email.value)
                : signupUrl;
            window.open(url, '_blank');
        }
        // Hand the typed address over so it needn't be retyped; sessionStorage rather than a query
        // param, since App.vue keys the router-view on the query.
        function forgotPassword() {
            if (email.value)
                sessionStorage.setItem('signinEmail', email.value);
            router.push({ path: '/signin/forgot' });
        }
        function clearError() {
            error.value = '';
        }
        function toEmail() {
            password.value = '';
            error.value = '';
            rejectedStatus.value = null;
            step.value = 'email';
        }
        function signIn() {
            if (!password.value)
                return;
            error.value = '';
            api.login(email.value, password.value, (success) => {
                if (success) {
                    cookies?.set('user', email.value, '1m');
                    cookies?.set('pass', password.value, '1m');
                    // Setting a PIN is the default, even for a device that already has one — only
                    // ?skipPin=true bypasses it, which the password reset flow passes because it has
                    // just set one itself.
                    const skipPin = route.query.skipPin === 'true';
                    router.push({ path: skipPin ? '/signin/whatsapp' : '/signin/pin' });
                }
                else {
                    error.value = 'Invalid email or password';
                }
            });
        }
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                (step.value === 'email' && !rejected.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                        _createElementVNode("img", {
                            class: "brandLogomark",
                            src: require('@/assets/signin/logomark.svg'),
                            alt: ""
                        }, null, 8, _hoisted_2),
                        _createElementVNode("img", {
                            class: "brandWordmark",
                            src: require('@/assets/signin/wordmark.svg'),
                            alt: "Money Guided"
                        }, null, 8, _hoisted_3)
                    ]))
                    : (_openBlock(), _createBlock(HeaderBlock, {
                        key: 1,
                        title: headerTitle.value,
                        titleSize: 20,
                        border: 0,
                        backButton: true,
                        onBack: toEmail
                    }, null, 8, ["title"])),
                _createElementVNode("div", _hoisted_4, [
                    (step.value === 'email' && !rejected.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[5] || (_cache[5] = [
                            _createElementVNode("p", { class: "signInHeadingTitle" }, "Sign in", -1),
                            _createElementVNode("p", { class: "signInHeadingText" }, "Enter the email address linked to your account", -1)
                        ])))
                        : (!rejected.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_6, "Enter your password"))
                            : _createCommentVNode("", true),
                    (step.value === 'email')
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                            _createElementVNode("div", {
                                class: _normalizeClass(["signInHelpCard", { rejected: rejected.value }])
                            }, [
                                (!rejected.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                        _createElementVNode("img", {
                                            src: require('@/assets/signin/help-avatar.svg'),
                                            alt: ""
                                        }, null, 8, _hoisted_8)
                                    ]))
                                    : _createCommentVNode("", true),
                                _createElementVNode("div", _hoisted_9, [
                                    _createElementVNode("div", _hoisted_10, [
                                        _createElementVNode("p", _hoisted_11, [
                                            (cardIcon.value)
                                                ? (_openBlock(), _createElementBlock("img", {
                                                    key: 0,
                                                    class: "helpInfoIcon",
                                                    src: cardIcon.value,
                                                    alt: ""
                                                }, null, 8, _hoisted_12))
                                                : _createCommentVNode("", true),
                                            _createTextVNode(" " + _toDisplayString(cardTitle.value), 1)
                                        ]),
                                        _createElementVNode("p", _hoisted_13, _toDisplayString(cardText.value), 1)
                                    ]),
                                    _createVNode(MGButton, {
                                        class: "helpCta",
                                        text: "Create an account",
                                        outline: !rejected.value,
                                        arrow: true,
                                        leftAlign: true,
                                        noCaps: true,
                                        onClick: activateMembership
                                    }, null, 8, ["outline"])
                                ])
                            ], 2),
                            _createElementVNode("div", {
                                class: _normalizeClass(["signInField", { hasError: error.value }])
                            }, [
                                _cache[6] || (_cache[6] = _createElementVNode("label", { for: "signInEmail" }, "Email address", -1)),
                                _createVNode(MGTextField, {
                                    id: "signInEmail",
                                    modelValue: email.value,
                                    "onUpdate:modelValue": [
                                        _cache[1] || (_cache[1] = ($event) => ((email).value = $event)),
                                        clearError
                                    ],
                                    type: "email",
                                    variant: "outlined",
                                    density: "compact",
                                    "hide-details": "",
                                    "single-line": "",
                                    autocomplete: "email",
                                    placeholder: "you@example.com",
                                    "bg-color": "white",
                                    onKeyup: _withKeys(toPassword, ["enter"])
                                }, {
                                    "append-inner": _withCtx(() => [
                                        (email.value)
                                            ? (_openBlock(), _createElementBlock("img", {
                                                key: 0,
                                                class: "fieldIcon",
                                                src: require('@/assets/signin/field-clear-20px.svg'),
                                                alt: "Clear",
                                                onClick: _cache[0] || (_cache[0] = ($event) => { email.value = ''; clearError(); })
                                            }, null, 8, _hoisted_14))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _: 1
                                }, 8, ["modelValue"]),
                                (error.value)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(error.value), 1))
                                    : _createCommentVNode("", true)
                            ], 2),
                            _createVNode(MGButton, {
                                class: "ctaButton",
                                text: "Continue with email",
                                arrow: true,
                                fullWidth: true,
                                disabled: !email.value || checking.value || !!error.value,
                                noCaps: true,
                                onClick: toPassword
                            }, null, 8, ["disabled"])
                        ], 64))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                            _createElementVNode("div", _hoisted_16, [
                                _createElementVNode("div", _hoisted_17, [
                                    _cache[7] || (_cache[7] = _createElementVNode("label", null, "Email address", -1)),
                                    _createElementVNode("div", _hoisted_18, [
                                        _createElementVNode("span", null, _toDisplayString(email.value), 1)
                                    ])
                                ]),
                                _createElementVNode("div", {
                                    class: _normalizeClass(["signInField", { hasError: error.value }])
                                }, [
                                    _cache[8] || (_cache[8] = _createElementVNode("label", { for: "signInPassword" }, "Password", -1)),
                                    _createVNode(MGTextField, {
                                        id: "signInPassword",
                                        modelValue: password.value,
                                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((password).value = $event)),
                                        type: showPassword.value ? 'text' : 'password',
                                        variant: "outlined",
                                        density: "compact",
                                        "hide-details": "",
                                        "single-line": "",
                                        autocomplete: "current-password",
                                        autofocus: "",
                                        "bg-color": "white",
                                        onKeyup: _withKeys(signIn, ["enter"])
                                    }, {
                                        "append-inner": _withCtx(() => [
                                            _createVNode(_component_v_icon, {
                                                class: "fieldIcon",
                                                icon: showPassword.value ? 'mdi-eye' : 'mdi-eye-off',
                                                "aria-label": showPassword.value ? 'Hide password' : 'Show password',
                                                onClick: _cache[2] || (_cache[2] = ($event) => (showPassword.value = !showPassword.value))
                                            }, null, 8, ["icon", "aria-label"]),
                                            (password.value)
                                                ? (_openBlock(), _createElementBlock("img", {
                                                    key: 0,
                                                    class: "fieldIcon",
                                                    src: require('@/assets/signin/field-clear-20px.svg'),
                                                    alt: "Clear",
                                                    onClick: _cache[3] || (_cache[3] = ($event) => (password.value = ''))
                                                }, null, 8, _hoisted_19))
                                                : _createCommentVNode("", true)
                                        ]),
                                        _: 1
                                    }, 8, ["modelValue", "type"])
                                ], 2)
                            ]),
                            (error.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                    _createElementVNode("p", _hoisted_21, [
                                        _createElementVNode("img", {
                                            class: "helpInfoIcon",
                                            src: require('@/assets/signin/info-red-16px.svg'),
                                            alt: ""
                                        }, null, 8, _hoisted_22),
                                        _cache[9] || (_cache[9] = _createTextVNode(" Incorrect email or password "))
                                    ]),
                                    _cache[10] || (_cache[10] = _createElementVNode("p", null, [
                                        _createTextVNode("That email or password doesn't look right."),
                                        _createElementVNode("br"),
                                        _createTextVNode("Please try again or reset your password.")
                                    ], -1))
                                ]))
                                : _createCommentVNode("", true),
                            _createElementVNode("div", _hoisted_23, [
                                _createVNode(MGButton, {
                                    class: "forgottenCta",
                                    text: "Forgotten password",
                                    noCaps: true,
                                    stacked: true,
                                    fullWidth: false,
                                    outline: false,
                                    transparent: true,
                                    arrow: true,
                                    inlineArrow: true,
                                    onClick: forgotPassword
                                }),
                                _createVNode(MGButton, {
                                    class: "ctaButton",
                                    text: "Sign in",
                                    arrow: true,
                                    fullWidth: true,
                                    disabled: !password.value,
                                    stacked: true,
                                    onClick: signIn
                                }, null, 8, ["disabled"])
                            ])
                        ], 64))
                ])
            ], 64));
        };
    }
});
